/**
 * ScenarioFilters Component
 * Filter controls for evaluation scenarios
 */

import React from 'react';
import { Select } from '@/components/ui/Select';
import { Search, Filter } from 'lucide-react';
import { useI18n } from '@/i18n';
import type { AgentTypeOption } from '@/types/evalScenarios';

interface ScenarioFiltersProps {
  filters: {
    agent_type: string;
    category: string;
    source: string;
    is_active: string;
    search: string;
  };
  onFilterChange: (filters: Partial<ScenarioFiltersProps['filters']>) => void;
  categories: string[];
  agentTypes?: AgentTypeOption[];
}

export const ScenarioFilters: React.FC<ScenarioFiltersProps> = ({
  filters,
  onFilterChange,
  categories,
  agentTypes = [],
}) => {
  const { messages } = useI18n();
  const t = messages.evalScenarios;

  const agentTypeOptions = [
    { value: '', label: t.allAgentTypes },
    ...agentTypes.map((a) => ({ value: a.value, label: a.label })),
  ];

  const categoriesArray = Array.isArray(categories) ? categories : [];
  const categoryOptions = [
    { value: '', label: t.allCategories },
    ...categoriesArray.map((cat) => ({ value: cat, label: cat })),
  ];

  const sourceOptions = [
    { value: '', label: t.allSources },
    { value: 'manual', label: t.sourceManual },
    { value: 'chat_promoted', label: t.sourceChatPromoted },
  ];

  const statusOptions = [
    { value: '', label: t.allStatus },
    { value: 'true', label: t.statusActive },
    { value: 'false', label: t.statusInactive },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div className="flex items-center gap-2 text-gray-700">
        <Filter className="w-4 h-4" />
        <span className="font-medium">{t.filters}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t.searchPlaceholder}
            value={filters.search}
            onChange={(e) => onFilterChange({ search: e.target.value })}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Agent Type */}
        <Select
          options={agentTypeOptions}
          value={filters.agent_type}
          onChange={(e) => onFilterChange({ agent_type: e.target.value })}
        />

        {/* Category */}
        <Select
          options={categoryOptions}
          value={filters.category}
          onChange={(e) => onFilterChange({ category: e.target.value })}
        />

        {/* Source */}
        <Select
          options={sourceOptions}
          value={filters.source}
          onChange={(e) => onFilterChange({ source: e.target.value })}
        />

        {/* Status */}
        <Select
          options={statusOptions}
          value={filters.is_active}
          onChange={(e) => onFilterChange({ is_active: e.target.value })}
        />
      </div>
    </div>
  );
};
